import { useCallback, useEffect, useRef, useState } from 'react'
import { UseFormHandleSubmit, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { ProcessReportDto } from 'src/dto/report.dto'
import { useGetFarmList } from 'src/hooks/api/farm'
import { useGetLotList } from 'src/hooks/api/lot'
import { useGetReport } from 'src/hooks/api/report'
import { useProcessReport } from 'src/hooks/api/report'
import { useAuth } from 'src/contexts/auth'

interface IUseReports {
  handleSubmit: UseFormHandleSubmit<any>
  setValue: UseFormSetValue<any>
  watch: UseFormWatch<any>
}

export const useReports = ({ handleSubmit, setValue, watch }: IUseReports) => {
  const { userFarm: farmId, username } = useAuth()
  const [reportId, setReportId] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)

  const { data: farmList, isLoading: isLoadingFarmList } = useGetFarmList()
  const { data: lotsList, isLoading: isLoadingLotsList } = useGetLotList()

  const { data: report, refetch: refetchReport } = useGetReport(username!, reportId!)
  const { mutateAsync: processReport } = useProcessReport()

  const intervalReportVerification = useRef<NodeJS.Timeout | null>(null)

  const formatDate = useCallback((date: Date | string): string => {
    if (date instanceof Date) {
      return date.toISOString().split('T')[0]
    }
    return date.toString()
  }, [])

  const onSubmit = handleSubmit(async (formData: ProcessReportDto) => {
    const reportConfigRequest = {
      ...formData,
      dataInicial: formatDate(formData.dataInicial),
      dataFinal: formatDate(formData.dataFinal),
      filtroIdades: Array.isArray(formData.filtroIdades) ? formData.filtroIdades.join(',') : '',
      filtroLotes: Array.isArray(formData.filtroLotes) ? formData.filtroLotes.join(',') : '',
      filtroSexo: Array.isArray(formData.filtroSexo) ? formData.filtroSexo.join(',') : '',
      idFazend: farmId!,
      username: username!
    }

    try {
      const response = await processReport(reportConfigRequest)

      if (response) {
        setReportId(response.idRelatorio)
        setIsPolling(true)
      }
    } catch (error) {
      setIsPolling(false)
      console.error('Erro ao gerar relatório:', error)
    }
  })

  const handleDownloadReport = useCallback(() => {
    if (report) {
      if (report?.arquivo) {
        downloadFile(report.arquivo)
      } else if (report.error) {
        console.error(report.error)
        toast.error('Erro ao processar relatório.')
      }
    }
  }, [report])

  const downloadFile = useCallback((fileUrl: string) => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.target = '_blank'
    link.click()
  }, [])

  useEffect(() => {
    if (reportId) {
      setIsPolling(true)
    }
  }, [reportId])

  useEffect(() => {
    if (report?.arquivo) {
      setIsPolling(false)
      handleDownloadReport()
      if (intervalReportVerification.current) {
        clearInterval(intervalReportVerification.current)
      }
    }

    if (isPolling) {
      intervalReportVerification.current = setInterval(async () => {
        await refetchReport()
      }, 5000)
    }

    return () => {
      if (intervalReportVerification.current) {
        clearInterval(intervalReportVerification.current)
      }
    }
  }, [report, handleDownloadReport, isPolling])

  useEffect(() => {
    setValue('filtroIdades', null)
    setValue('filtroLotes', null)
    setValue('filtroSexo', null)
  }, [watch('tipo')])

  return {
    onSubmit,
    isLoading: isLoadingFarmList || isLoadingLotsList,
    isProcessingReport: isPolling,
    report,
    farmList,
    lotsList
  }
}
